import { queryClient } from "common/queryClient";
import { queryKeys } from "common/queryKeys";
import { onlineManager } from "@tanstack/react-query";
import { globalNotify } from "common/utils/notificationBus";
import {
  isOfflineWriteBlockedError,
  OFFLINE_WRITE_BLOCKED_MESSAGE,
} from "common/utils/offlineWriteGuard";
import { useOnlineStatus } from "common/utils/useOnlineStatus";
import { endOfMonth, format, startOfMonth } from "date-fns";
import { useCurrentFarmAnimalGroups, useFields } from "model";
import { getAllFieldsForAnimalGroupIdFromArray } from "model/fieldHelpers";
import { useCallback, useEffect, useRef, useState } from "react";
import type {
  FeedBudgetPlanListItem,
  FeedBudgetPlanWithVersion,
} from "services";
import {
  activateFeedBudgetPlan,
  createFeedBudgetPlan,
  createFeedBudgetPlanAnimalGroupBudget,
  deleteFeedBudgetPlan,
  deleteFeedBudgetPlanAnimalGroupBudget,
  getFeedBudgetPlan,
  listFeedBudgetPlans,
  updateFeedBudgetPlan,
  updateFeedBudgetPlanAnimalGroupBudget,
} from "services";
import { useAppStore } from "stores";
import { v4 as uuid } from "uuid";
import {
  createDefaultGrazingMonths,
  createDefaultSupplementPlans,
  DEFAULT_INITIAL_PASTURE_COVER,
  DEFAULT_SUPPLEMENT_FEED_TYPES,
  DEFAULT_SUPPLEMENT_SUBSTITUTION_RATES,
  DEFAULT_TARGET_RESIDUAL,
  TYPICAL_ALLOCATIONS,
  TYPICAL_WEIGHTS,
} from "./constants";
import type {
  FeedBudgetPlan,
  StockClassMonthEntry,
  StockClassMonthlyPlan,
} from "./types";

function deepClone<T>(value: T): T {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }
  return JSON.parse(JSON.stringify(value)) as T;
}

/**
 * Ensures every stock class in a multi-year plan has year-1 entries.
 * If a stock class only has year-0 entries and the plan spans 2+ years,
 * copies those entries to year-1 so the second year shows populated data.
 * Returns the (possibly mutated) plan and the IDs of groups that were changed.
 */
function normalizeMultiYearPlan(plan: FeedBudgetPlanWithVersion): {
  plan: FeedBudgetPlanWithVersion;
  dirtyGroupIds: string[];
  metadataDirty: boolean;
} {
  const startYear = plan.startDate
    ? new Date(plan.startDate).getFullYear()
    : null;
  const endYear = plan.endDate ? new Date(plan.endDate).getFullYear() : null;
  if (!startYear || !endYear || endYear <= startYear) {
    return { plan, dirtyGroupIds: [], metadataDirty: false };
  }

  // Backfill year-1 grazing months (growth rate + utilisation) by copying year-0
  const grazingHasYear1 = plan.grazingMonths.some(
    (gm) => (gm.yearOffset ?? 0) === 1
  );
  const grazingMonths = grazingHasYear1
    ? plan.grazingMonths
    : [
        ...plan.grazingMonths,
        ...plan.grazingMonths
          .filter((gm) => (gm.yearOffset ?? 0) === 0)
          .map((gm) => ({ ...gm, yearOffset: 1 })),
      ];

  // Backfill year-1 stock class entries by copying year-0
  const dirtyGroupIds: string[] = [];
  const animalGroupBudgets = plan.animalGroupBudgets.map((agb) => {
    const needsYear1 = agb.stockClassPlans.some(
      (sc) => !sc.months.some((m) => (m.yearOffset ?? 0) === 1)
    );
    if (!needsYear1) return agb;

    dirtyGroupIds.push(agb.animalGroupId);
    return {
      ...agb,
      stockClassPlans: agb.stockClassPlans.map((sc) => {
        const hasYear1 = sc.months.some((m) => (m.yearOffset ?? 0) === 1);
        if (hasYear1) return sc;
        const year0Months = sc.months.filter((m) => (m.yearOffset ?? 0) === 0);
        const year1Months: StockClassMonthEntry[] = year0Months.map((m) => ({
          ...m,
          yearOffset: 1,
        }));
        return { ...sc, months: [...sc.months, ...year1Months] };
      }),
    };
  });

  const metadataDirty = !grazingHasYear1;
  return {
    plan: { ...plan, grazingMonths, animalGroupBudgets },
    dirtyGroupIds,
    metadataDirty,
  };
}

export function useFeedBudgetPlan() {
  const farmId = useAppStore((s) => s.currentFarmId);
  const online = useOnlineStatus();
  const currentYear = new Date().getFullYear();
  const [farmPlansList, setFarmPlansList] = useState<FeedBudgetPlanListItem[]>(
    []
  );
  const [plan, setPlan] = useState<FeedBudgetPlanWithVersion | null>(null);
  const [draftPlan, setDraftPlan] = useState<FeedBudgetPlanWithVersion | null>(
    null
  );
  const [activePlanId, setActivePlanIdState] = useState<string | null>(null);
  const [viewedPlanId, setViewedPlanIdState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [metadataDirty, setMetadataDirty] = useState(false);
  const [dirtyGroups, setDirtyGroups] = useState<Set<string>>(new Set());
  // Always-current ref to the last-saved plan — used by discardGroupChanges to
  // avoid stale closure issues that arise when plan is captured in useCallback.
  const savedPlanRef = useRef<FeedBudgetPlanWithVersion | null>(null);
  savedPlanRef.current = plan;
  // Always-current ref to the editable draft — used by replicateGroupToOtherPlans
  // which may run after savePlan triggers a re-render.
  const draftPlanRef = useRef<FeedBudgetPlanWithVersion | null>(null);
  draftPlanRef.current = draftPlan;
  const farmPlansListRef = useRef<FeedBudgetPlanListItem[]>([]);
  farmPlansListRef.current = farmPlansList;

  // Cache a just-created plan so refreshViewedPlan can skip the redundant fetch.
  const freshPlanRef = useRef<FeedBudgetPlanWithVersion | null>(null);

  // Get existing farm data
  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();
  const { data: allFields = [] } = useFields();

  const effectiveFarmId = farmId ?? "";

  const refreshPlansList = useCallback(async () => {
    if (!effectiveFarmId || !onlineManager.isOnline()) return;
    setIsLoading(true);
    try {
      const list = await listFeedBudgetPlans(effectiveFarmId);
      queryClient.setQueryData(
        queryKeys.feedBudgetPlans.list(effectiveFarmId),
        list
      );
      // Preserve stable order: keep existing positions, append new plans at end
      setFarmPlansList((prev) => {
        const retained = prev
          .filter((p) => list.some((l) => l.guid === p.guid))
          // find is guaranteed non-null here because we filtered for it above
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          .map((p) => list.find((l) => l.guid === p.guid)!);
        const added = list.filter((l) => !prev.some((p) => p.guid === l.guid));
        return [...retained, ...added];
      });
      const active = list.find((p) => p.isActive)?.guid ?? null;
      setActivePlanIdState(active);
      const validViewed =
        viewedPlanId && list.some((p) => p.guid === viewedPlanId);
      const nextViewed = validViewed
        ? viewedPlanId
        : active ?? list[0]?.guid ?? null;
      setViewedPlanIdState(nextViewed);
    } catch {
      // Offline or transient error — keep showing the last loaded list.
    } finally {
      setIsLoading(false);
    }
  }, [effectiveFarmId, viewedPlanId]);

  const refreshViewedPlan = useCallback(async () => {
    if (!viewedPlanId) {
      setPlan(null);
      setDraftPlan(null);
      return;
    }
    // If we have a freshly-created plan for this id, use it directly — no refetch needed.
    if (freshPlanRef.current?.guid === viewedPlanId) {
      const fresh = freshPlanRef.current;
      freshPlanRef.current = null;
      const {
        plan: normalized,
        dirtyGroupIds,
        metadataDirty: normMeta,
      } = normalizeMultiYearPlan(fresh);
      setPlan(normalized);
      setDraftPlan(deepClone(normalized));
      setMetadataDirty(normMeta);
      setDirtyGroups(new Set(dirtyGroupIds));
      setSaveError(null);
      return;
    }
    if (!onlineManager.isOnline()) return;
    setIsLoading(true);
    try {
      const full = await getFeedBudgetPlan(viewedPlanId);
      queryClient.setQueryData(
        queryKeys.feedBudgetPlans.detail(viewedPlanId),
        full
      );
      const {
        plan: normalized,
        dirtyGroupIds,
        metadataDirty: normMeta,
      } = normalizeMultiYearPlan(full);
      setPlan(normalized);
      setDraftPlan(deepClone(normalized));
      setMetadataDirty(normMeta);
      setDirtyGroups(new Set(dirtyGroupIds));
      setSaveError(null);
    } catch {
      // Offline or transient error — keep the last loaded draft.
    } finally {
      setIsLoading(false);
    }
  }, [viewedPlanId]);

  useEffect(() => {
    if (!online) return;
    void refreshPlansList();
  }, [refreshPlansList, online]);

  useEffect(() => {
    if (!online) return;
    void refreshViewedPlan();
  }, [refreshViewedPlan, online]);

  const setViewedPlanId = useCallback((id: string | null) => {
    setViewedPlanIdState(id);
  }, []);

  const setActivePlanId = useCallback(
    async (id: string) => {
      if (id === activePlanId) return;
      await activateFeedBudgetPlan(id);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.feedBudgetPlans.all,
      });
      await refreshPlansList();
    },
    [activePlanId, refreshPlansList]
  );

  const buildDefaultAnimalGroupBudgets = useCallback(() => {
    // Stock class plans are authored in the planner/wizard (the feed budget is
    // the single source of truth) — a from-scratch plan starts with empty
    // groups, not seeded from backend stock class records.
    return animalGroups.map((ag, index) => {
      const groupFields = getAllFieldsForAnimalGroupIdFromArray(
        allFields,
        ag.guid
      );
      const grazingAreaHa = groupFields.reduce(
        (sum, f) => sum + (f.area || 0) / 10000,
        0
      );

      return {
        guid: uuid(),
        animalGroupId: ag.guid,
        animalGroupName: ag.name,
        sortOrder: index,
        grazingAreaHa: Math.round(grazingAreaHa * 100) / 100,
        stockClassPlans: [] as StockClassMonthlyPlan[],
        supplementFeedTypes: [...DEFAULT_SUPPLEMENT_FEED_TYPES],
        supplementPlans: createDefaultSupplementPlans(),
      };
    });
  }, [animalGroups, allFields]);

  const createPlan = useCallback(
    async (options?: {
      name?: string;
      startDate?: string;
      endDate?: string;
    }) => {
      if (!effectiveFarmId) return;

      const defaultStart = format(
        startOfMonth(new Date(currentYear, 0, 1)),
        "yyyy-MM-dd"
      );
      const defaultEnd = format(
        endOfMonth(new Date(currentYear, 11, 1)),
        "yyyy-MM-dd"
      );

      const startDate = options?.startDate ?? defaultStart;
      const endDate = options?.endDate ?? defaultEnd;
      const startYear = new Date(startDate).getFullYear();

      // A new feed budget duplicates the active one — preserving the farmer's
      // tuned counts, allocations, supplements, growth rates and grazing areas
      // (including the per-month areas, which change across the year). Only the
      // name and date range differ. Fall back to seeding from stock classes when
      // there is no active plan to copy (e.g. the very first budget).
      let source: FeedBudgetPlanWithVersion | null = null;
      if (activePlanId) {
        if (savedPlanRef.current?.guid === activePlanId) {
          source = savedPlanRef.current;
        } else if (onlineManager.isOnline()) {
          try {
            source = await getFeedBudgetPlan(activePlanId);
          } catch {
            source = null;
          }
        }
      }

      const nextPlan = {
        farmGuid: effectiveFarmId,
        name: options?.name || `Feed Budget ${startYear}`,
        startDate,
        endDate,
        initialPastureCoverKgHa:
          source?.initialPastureCoverKgHa ?? DEFAULT_INITIAL_PASTURE_COVER,
        targetResidualKgHa:
          source?.targetResidualKgHa ?? DEFAULT_TARGET_RESIDUAL,
        grazingMonths: source
          ? deepClone(source.grazingMonths)
          : createDefaultGrazingMonths(),
        supplementSubstitutionRates: source
          ? deepClone(source.supplementSubstitutionRates)
          : { ...DEFAULT_SUPPLEMENT_SUBSTITUTION_RATES },
        // createFeedBudgetPlan strips the client-side budget guids; the server
        // assigns new ones, so this is a true copy that references the same
        // stock classes.
        animalGroupBudgets: source
          ? deepClone(source.animalGroupBudgets)
          : buildDefaultAnimalGroupBudgets(),
      };

      setSaveError(null);

      try {
        const created = await createFeedBudgetPlan(nextPlan);
        // Cache the full plan so refreshViewedPlan (triggered below) skips the refetch.
        freshPlanRef.current = created;
        // Add to list directly — no need for a round-trip to avoid toggling isLoading.
        setFarmPlansList((prev) => [
          ...prev,
          {
            guid: created.guid,
            farmGuid: effectiveFarmId,
            name: created.name,
            startDate: created.startDate,
            endDate: created.endDate,
            isActive: created.isActive,
            version: created.version,
            createdAt: created.createdAt,
            updatedAt: created.updatedAt,
          },
        ]);
        setViewedPlanIdState(created.guid);
        if (created.isActive) {
          setActivePlanIdState(created.guid);
        }
        await queryClient.invalidateQueries({
          queryKey: queryKeys.feedBudgetPlans.all,
        });
      } catch (err) {
        if (isOfflineWriteBlockedError(err)) {
          setSaveError(null);
        } else {
          console.error("[createPlan] API error:", err);
          setSaveError("Failed to create feed budget plan");
        }
        throw err;
      }
    },
    [
      currentYear,
      effectiveFarmId,
      activePlanId,
      buildDefaultAnimalGroupBudgets,
      farmPlansList.length,
    ]
  );

  /**
   * Ensure the currently-viewed plan has a budget entry for the given animal group.
   * Used when a new animal group is created after a plan already exists — the plan's
   * animalGroupBudgets are only built at creation time, so a fresh group needs one
   * added server-side. No-op (resolves true) if a budget already exists. Returns false
   * if there is no viewed plan to add to.
   */
  const ensureGroupBudget = useCallback(
    async (animalGroupId: string, animalGroupName: string) => {
      if (!viewedPlanId) return false;
      const current = draftPlan;
      if (current?.guid === viewedPlanId) {
        const exists = current.animalGroupBudgets.some(
          (agb) => agb.animalGroupId === animalGroupId
        );
        if (exists) return true;
      }

      const groupFields = getAllFieldsForAnimalGroupIdFromArray(
        allFields,
        animalGroupId
      );
      const grazingAreaHa =
        Math.round(
          groupFields.reduce((sum, f) => sum + (f.area || 0) / 10000, 0) * 100
        ) / 100;

      try {
        const created = await createFeedBudgetPlanAnimalGroupBudget({
          planGuid: viewedPlanId,
          animalGroupId,
          animalGroupName,
          grazingAreaHa,
          stockClassPlans: [],
          supplementFeedTypes: [...DEFAULT_SUPPLEMENT_FEED_TYPES],
          supplementPlans: createDefaultSupplementPlans(),
        });
        // Surface the new budget (with its server guid) in the draft immediately.
        freshPlanRef.current = created;
        await refreshViewedPlan();
        return true;
      } catch (err) {
        if (isOfflineWriteBlockedError(err)) return false;
        console.error("[ensureGroupBudget] API error:", err);
        return false;
      }
    },
    [viewedPlanId, draftPlan, allFields, refreshViewedPlan]
  );

  /**
   * Remove the currently-viewed plan's budget entry for the given animal group.
   * Used when discarding a freshly-created group. No-op if no such budget exists.
   */
  const removeGroupBudget = useCallback(
    async (animalGroupId: string) => {
      const current = draftPlan;
      if (current?.guid !== viewedPlanId) return;
      const budget = current.animalGroupBudgets.find(
        (agb) => agb.animalGroupId === animalGroupId
      );
      if (!budget?.guid) return;
      try {
        await deleteFeedBudgetPlanAnimalGroupBudget(budget.guid);
        await refreshViewedPlan();
      } catch (err) {
        if (!isOfflineWriteBlockedError(err)) {
          console.error("[removeGroupBudget] API error:", err);
        }
      }
    },
    [draftPlan, viewedPlanId, refreshViewedPlan]
  );

  /**
   * Copy the currently-viewed plan's budget for a group (including its stock class
   * plans and supplements) onto every OTHER feed budget plan on the farm. Used when
   * a newly-created group should appear in all existing budgets, not just the active
   * one. Idempotent per plan (the API skips plans that already have the group).
   */
  const replicateGroupToOtherPlans = useCallback(
    async (animalGroupId: string) => {
      const source = draftPlanRef.current;
      if (!source) return;
      const budget = source.animalGroupBudgets.find(
        (agb) => agb.animalGroupId === animalGroupId
      );
      if (!budget) return;

      const otherPlans = farmPlansListRef.current.filter(
        (p) => p.guid !== source.guid
      );
      for (const otherPlan of otherPlans) {
        try {
          await createFeedBudgetPlanAnimalGroupBudget({
            planGuid: otherPlan.guid,
            animalGroupId,
            animalGroupName: budget.animalGroupName,
            grazingAreaHa: budget.grazingAreaHa,
            monthlyGrazingAreaHa: budget.monthlyGrazingAreaHa as
              | Record<string, number>
              | undefined,
            feedTypeUtilisation: budget.feedTypeUtilisation,
            fieldFeedingUtilisationPercent:
              budget.fieldFeedingUtilisationPercent,
            stockClassPlans: budget.stockClassPlans,
            supplementFeedTypes: budget.supplementFeedTypes,
            supplementPlans: budget.supplementPlans,
          });
        } catch (err) {
          if (!isOfflineWriteBlockedError(err)) {
            console.error("[replicateGroupToOtherPlans] API error:", err);
          }
        }
      }
    },
    []
  );

  const duplicatePlan = useCallback(
    (_guid: string) => {
      const source = draftPlan;
      if (!source) return;
      void createFeedBudgetPlan({
        farmGuid: effectiveFarmId,
        name: `${source.name} (copy)`,
        startDate: source.startDate,
        endDate: source.endDate,
        initialPastureCoverKgHa: source.initialPastureCoverKgHa,
        targetResidualKgHa: source.targetResidualKgHa,
        grazingMonths: source.grazingMonths,
        supplementSubstitutionRates: source.supplementSubstitutionRates,
        animalGroupBudgets: source.animalGroupBudgets,
      }).then((created) => {
        setViewedPlanIdState(created.guid);
        setPlan(created);
        setDraftPlan(created);
        void refreshPlansList();
      });
    },
    [draftPlan, effectiveFarmId, refreshPlansList]
  );

  const deletePlan = useCallback(
    (guid: string) => {
      void deleteFeedBudgetPlan(guid).then(() => {
        if (viewedPlanId === guid) {
          setViewedPlanIdState(null);
          setPlan(null);
          setDraftPlan(null);
        }
        void refreshPlansList();
      });
    },
    [refreshPlansList, viewedPlanId]
  );

  /** Update metadata (name, dates) of any plan by ID */
  const updatePlanMetadata = useCallback(
    (
      guid: string,
      metadata: Partial<Pick<FeedBudgetPlan, "name" | "startDate" | "endDate">>
    ) => {
      setDraftPlan((prev) => {
        if (!prev || prev.guid !== guid) return prev;
        return { ...prev, ...metadata };
      });
      setMetadataDirty(true);
    },
    []
  );

  /** Save plan metadata (name, dates) immediately to the API */
  const savePlanMetadata = useCallback(
    async (
      guid: string,
      metadata: Pick<FeedBudgetPlan, "name" | "startDate" | "endDate">
    ) => {
      if (!farmPlansList.some((p) => p.guid === guid)) return;

      let sourcePlan: FeedBudgetPlanWithVersion | null =
        draftPlan?.guid === guid
          ? draftPlan
          : plan?.guid === guid
          ? plan
          : null;

      if (!sourcePlan) {
        if (!onlineManager.isOnline()) {
          globalNotify("You need to be online to use this.", "error");
          throw new TypeError(OFFLINE_WRITE_BLOCKED_MESSAGE);
        }
        sourcePlan = await getFeedBudgetPlan(guid);
      }

      setIsSaving(true);
      setSaveError(null);
      try {
        const updated = await updateFeedBudgetPlan(guid, {
          name: metadata.name,
          startDate: metadata.startDate,
          endDate: metadata.endDate,
          initialPastureCoverKgHa: sourcePlan.initialPastureCoverKgHa,
          targetResidualKgHa: sourcePlan.targetResidualKgHa,
          grazingMonths: sourcePlan.grazingMonths,
          supplementSubstitutionRates: sourcePlan.supplementSubstitutionRates,
          version: sourcePlan.version,
        });
        if (draftPlan?.guid === guid) {
          setDraftPlan((prev) =>
            prev?.guid === guid
              ? { ...prev, ...metadata, version: updated.version }
              : prev
          );
          setPlan((prev) =>
            prev?.guid === guid
              ? { ...prev, ...metadata, version: updated.version }
              : prev
          );
          setMetadataDirty(false);
        }
        await refreshViewedPlan();
        await refreshPlansList();
      } catch (err) {
        if (isOfflineWriteBlockedError(err)) {
          setSaveError(null);
        } else {
          console.error("[savePlanMetadata] API error:", err);
          setSaveError("Failed to save feed budget plan");
        }
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [draftPlan, plan, farmPlansList, refreshPlansList, refreshViewedPlan]
  );

  /** Update the currently viewed plan */
  const updatePlan = useCallback(
    (updater: (plan: FeedBudgetPlan) => FeedBudgetPlan) => {
      setDraftPlan((prev) => {
        if (!prev || !viewedPlanId || prev.guid !== viewedPlanId) return prev;
        const next = updater(prev);
        const nextDirty = new Set(dirtyGroups);
        const prevGroups = new Map(
          prev.animalGroupBudgets.map((g) => [g.animalGroupId, g])
        );
        for (const g of next.animalGroupBudgets) {
          const existing = prevGroups.get(g.animalGroupId);
          if (!existing || JSON.stringify(existing) !== JSON.stringify(g)) {
            nextDirty.add(g.animalGroupId);
          }
        }
        if (
          prev.name !== next.name ||
          prev.startDate !== next.startDate ||
          prev.endDate !== next.endDate ||
          prev.initialPastureCoverKgHa !== next.initialPastureCoverKgHa ||
          prev.targetResidualKgHa !== next.targetResidualKgHa ||
          JSON.stringify(prev.grazingMonths) !==
            JSON.stringify(next.grazingMonths) ||
          JSON.stringify(prev.supplementSubstitutionRates) !==
            JSON.stringify(next.supplementSubstitutionRates)
        ) {
          setMetadataDirty(true);
        }
        setDirtyGroups(nextDirty);
        return next;
      });
    },
    [dirtyGroups, viewedPlanId]
  );

  const addStockClassToGroup = useCallback(
    (
      animalGroupId: string,
      name: string,
      defaultStockClassName?: string,
      initialValues?: {
        animalCount?: number;
        weightKg?: number;
        allocationPercent?: number;
        /** Per-month allocation % (12 values). Overrides allocationPercent when provided. */
        allocationByMonth?: number[];
        /** Per-month animal count (12 values). Overrides animalCount when provided. */
        animalCountByMonth?: number[];
      }
    ) => {
      const animalCount = initialValues?.animalCount ?? 0;
      const weightKg =
        initialValues?.weightKg ??
        TYPICAL_WEIGHTS[defaultStockClassName ?? ""] ??
        65;

      // The feed budget is the single source of truth — the stock class lives
      // entirely in the plan's stockClassPlans, keyed by this client-generated
      // id. No backend StockClass record is created.
      const stockClassId = uuid();

      updatePlan((plan) => {
        // If the plan spans two calendar years, create entries for both years.
        const planStartYear = plan.startDate
          ? new Date(plan.startDate).getFullYear()
          : null;
        const planEndYear = plan.endDate
          ? new Date(plan.endDate).getFullYear()
          : null;
        const planSpans2Years =
          planStartYear !== null &&
          planEndYear !== null &&
          planEndYear > planStartYear;

        // Respect explicit 24-entry animalCountByMonth (spawned classes);
        // otherwise extend to 24 if plan spans 2 years.
        const providedCount = initialValues?.animalCountByMonth?.length ?? 12;
        const entryCount =
          providedCount >= 24 ? providedCount : planSpans2Years ? 24 : 12;

        return {
          ...plan,
          animalGroupBudgets: plan.animalGroupBudgets.map((agb) =>
            agb.animalGroupId === animalGroupId
              ? {
                  ...agb,
                  stockClassPlans: [
                    ...agb.stockClassPlans,
                    {
                      stockClassId,
                      stockClassName: name,
                      defaultStockClassName,
                      months: Array.from({ length: entryCount }, (_, i) => {
                        const month = i % 12;
                        const entry: StockClassMonthEntry = {
                          month,
                          animalCount:
                            initialValues?.animalCountByMonth?.[i] ??
                            initialValues?.animalCountByMonth?.[i % 12] ??
                            animalCount,
                          averageWeightKg: weightKg,
                          allocationPercent:
                            initialValues?.allocationByMonth?.[month] ??
                            initialValues?.allocationPercent ??
                            TYPICAL_ALLOCATIONS[defaultStockClassName ?? ""] ??
                            2.5,
                        };
                        if (i >= 12) entry.yearOffset = 1;
                        return entry;
                      }),
                    },
                  ],
                }
              : agb
          ),
        };
      });
    },
    [updatePlan]
  );

  const removeStockClassFromGroup = useCallback(
    (animalGroupId: string, stockClassId: string) => {
      // Removing from the plan is sufficient — the stock class only lives in the
      // feed budget, there is no backend StockClass record to delete.
      updatePlan((plan) => ({
        ...plan,
        animalGroupBudgets: plan.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === animalGroupId
            ? {
                ...agb,
                stockClassPlans: agb.stockClassPlans.filter(
                  (sc) => sc.stockClassId !== stockClassId
                ),
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const addSupplementFeedType = useCallback(
    (
      animalGroupId: string,
      name: string,
      substitutionRate: number,
      explicitId?: string
    ) => {
      const id = explicitId ?? uuid();
      updatePlan((plan) => ({
        ...plan,
        animalGroupBudgets: plan.animalGroupBudgets.map((agb) =>
          agb.animalGroupId === animalGroupId
            ? {
                ...agb,
                supplementFeedTypes: [
                  ...agb.supplementFeedTypes,
                  { id, name, substitutionRate },
                ],
                supplementPlans: agb.supplementPlans.map((s) => ({
                  ...s,
                  amounts: { ...s.amounts, [id]: 0 },
                })),
              }
            : agb
        ),
      }));
    },
    [updatePlan]
  );

  const removeSupplementFeedType = useCallback(
    (animalGroupId: string, feedTypeId: string) => {
      updatePlan((plan) => ({
        ...plan,
        animalGroupBudgets: plan.animalGroupBudgets.map((agb) => {
          if (agb.animalGroupId !== animalGroupId) return agb;
          return {
            ...agb,
            supplementFeedTypes: agb.supplementFeedTypes.filter(
              (ft) => ft.id !== feedTypeId
            ),
            supplementPlans: agb.supplementPlans.map((s) => {
              const { [feedTypeId]: _removed, ...rest } = s.amounts;
              return { ...s, amounts: rest };
            }),
          };
        }),
      }));
    },
    [updatePlan]
  );

  /** Reset a specific animal group's budget to the last-saved state, discarding local edits. */
  const discardGroupChanges = useCallback((animalGroupId: string) => {
    const saved = savedPlanRef.current;
    if (!saved) return;
    const originalBudget = saved.animalGroupBudgets.find(
      (g) => g.animalGroupId === animalGroupId
    );
    setDraftPlan((draft) => {
      if (!draft) return draft;
      return {
        ...draft,
        grazingMonths: deepClone(saved.grazingMonths),
        animalGroupBudgets: draft.animalGroupBudgets.map((g) =>
          g.animalGroupId === animalGroupId && originalBudget
            ? deepClone(originalBudget)
            : g
        ),
      };
    });
    setDirtyGroups((prev) => {
      const next = new Set(prev);
      next.delete(animalGroupId);
      return next;
    });
    setMetadataDirty(false);
  }, []);

  const savePlan = useCallback(async () => {
    if (!draftPlan || !viewedPlanId) return;
    if (!metadataDirty && dirtyGroups.size === 0) return;
    setIsSaving(true);
    setSaveError(null);
    try {
      let version = plan?.version ?? draftPlan.version;
      if (metadataDirty) {
        const updated = await updateFeedBudgetPlan(viewedPlanId, {
          name: draftPlan.name,
          startDate: draftPlan.startDate,
          endDate: draftPlan.endDate,
          initialPastureCoverKgHa: draftPlan.initialPastureCoverKgHa,
          targetResidualKgHa: draftPlan.targetResidualKgHa,
          grazingMonths: draftPlan.grazingMonths,
          supplementSubstitutionRates: draftPlan.supplementSubstitutionRates,
          version,
        });
        version = updated.version;
      }

      for (const animalGroupId of dirtyGroups) {
        const budget = draftPlan.animalGroupBudgets.find(
          (g) => g.animalGroupId === animalGroupId
        );
        if (!budget?.guid) continue;
        await updateFeedBudgetPlanAnimalGroupBudget(budget.guid, {
          animalGroupName: budget.animalGroupName,
          grazingAreaHa: budget.grazingAreaHa,
          sortOrder: budget.sortOrder,
          monthlyGrazingAreaHa: budget.monthlyGrazingAreaHa as
            | Record<string, number>
            | undefined,
          feedTypeUtilisation: budget.feedTypeUtilisation,
          fieldFeedingUtilisationPercent: budget.fieldFeedingUtilisationPercent,
          stockClassPlans: budget.stockClassPlans,
          supplementFeedTypes: budget.supplementFeedTypes,
          supplementPlans: budget.supplementPlans,
          version,
        });
        version += 1;
      }

      await refreshViewedPlan();
      await refreshPlansList();
    } catch (err) {
      if (isOfflineWriteBlockedError(err)) {
        setSaveError(null);
      } else {
        console.error("[savePlan] API error:", err);
        setSaveError("Failed to save feed budget plan");
      }
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [
    dirtyGroups,
    draftPlan,
    metadataDirty,
    plan?.version,
    refreshPlansList,
    refreshViewedPlan,
    viewedPlanId,
  ]);

  const dismissSaveError = useCallback(() => setSaveError(null), []);

  return {
    /** The currently viewed/edited plan */
    plan: draftPlan as FeedBudgetPlan | null,
    farmPlans: farmPlansList.map((p) => ({
      guid: p.guid,
      farm: effectiveFarmId,
      name: p.name,
      startDate: p.startDate,
      endDate: p.endDate,
      initialPastureCoverKgHa: 0,
      targetResidualKgHa: 0,
      grazingMonths: [],
      supplementSubstitutionRates: DEFAULT_SUPPLEMENT_SUBSTITUTION_RATES,
      animalGroupBudgets: [],
    })),
    /** The "starred"/default plan shown in the planner card */
    activePlanId,
    setActivePlanId,
    /** The plan currently being viewed (may differ from activePlanId) */
    viewedPlanId,
    setViewedPlanId,
    createPlan,
    ensureGroupBudget,
    removeGroupBudget,
    replicateGroupToOtherPlans,
    duplicatePlan,
    deletePlan,
    updatePlan,
    updatePlanMetadata,
    savePlanMetadata,
    addStockClassToGroup,
    removeStockClassFromGroup,
    addSupplementFeedType,
    removeSupplementFeedType,
    isLoading,
    isSaving,
    isDirty: metadataDirty || dirtyGroups.size > 0,
    metadataDirty,
    dirtyGroups,
    discardGroupChanges,
    saveError,
    dismissSaveError,
    savePlan,
  };
}
